import Vue from "vue";
import Vuex from "vuex";
import localforage from "localforage";
import { supabase } from "@/supabase";
import * as categories from "@/store/modules/categories";
import * as racers from "@/store/modules/racers";
import * as sync from "@/store/modules/sync";
Vue.use(Vuex);

// Dades antigues que vuex-persist guardava al navegador (només per migrar-les)
const legacyDb = localforage.createInstance({ name: "racemrt", storeName: "data" });
const LEGACY_KEY = "race_mrt";

// Còpia de l'última versió coneguda de les dades, per poder arrencar sense connexió
const CACHE_KEY = "race_mrt_cache";

function readCache() {
    try {
        return JSON.parse(localStorage.getItem(CACHE_KEY));
    } catch (e) {
        return null;
    }
}

let cacheTimer = null;
function persistPlugin(store) {
    store.subscribe((mutation, state) => {
        if (mutation.type === "sync/push" || mutation.type === "sync/shift") {
            sync.savePending(state.sync.pending);
            return;
        }
        if (!/^(racers|categories)\//.test(mutation.type)) return;

        clearTimeout(cacheTimer);
        cacheTimer = setTimeout(() => {
            try {
                localStorage.setItem(
                    CACHE_KEY,
                    JSON.stringify({
                        categories: state.categories.list,
                        racers: state.racers.list,
                    })
                );
            } catch (e) {
                console.warn("No s'ha pogut guardar la còpia local", e);
            }
        }, 500);
    });
}

let subscribed = false;

export default new Vuex.Store({
    actions: {
        async init(context) {
            if (!supabase) {
                context.commit("sync/setStatus", "config");
                return;
            }

            context.commit("sync/setStatus", "loading");
            try {
                await context.dispatch("reload");
            } catch (error) {
                const cached = readCache();
                if (!cached) {
                    context.commit("sync/setStatus", "error");
                    return;
                }
                context.commit("categories/setCategories", cached.categories);
                context.commit("racers/setRacers", cached.racers);
                context.commit("sync/setOnline", false);
            }

            await context.dispatch("migrateLegacyData");
            context.dispatch("subscribe");
            context.dispatch("sync/flush");
            context.commit("sync/setStatus", "ready");
        },

        async reload(context) {
            await Promise.all([
                context.dispatch("categories/loadCategories"),
                context.dispatch("racers/loadRacers"),
            ]);
            context.commit("sync/setOnline", true);
        },

        // Torna a llegir tot de Supabase (p. ex. després d'estar sense connexió) i envia els pendents
        async resync(context) {
            try {
                await context.dispatch("reload");
            } catch (e) {
                context.commit("sync/setOnline", false);
            }
            context.dispatch("sync/flush");
        },

        // Si Supabase no té corredors i aquest navegador en té de l'antic LocalForage, els puja (sense voltes)
        async migrateLegacyData(context) {
            if (Object.keys(context.state.racers.list).length) return;

            let legacy;
            try {
                legacy = await legacyDb.getItem(LEGACY_KEY);
                if (typeof legacy === "string") legacy = JSON.parse(legacy);
            } catch (e) {
                console.warn("No s'han pogut llegir les dades locals antigues", e);
                return;
            }

            const legacyRacers = Object.values(legacy?.racers?.list || {});
            if (!legacyRacers.length) return;

            const known = context.state.categories.list;
            const rows = legacyRacers
                .filter((racer) => known[racer.category])
                .map((racer) => ({ id: racer.id, ...racers.toFields(racer), laps: [] }));
            const { error } = await supabase.from("racers").upsert(rows);
            if (error) {
                console.error("Error migrant les dades locals a Supabase", error);
                return;
            }

            console.log(`Migrats ${rows.length} corredors de LocalForage a Supabase`);
            await legacyDb.removeItem(LEGACY_KEY);
            await context.dispatch("racers/loadRacers");
        },

        // Manté l'estat sincronitzat amb els canvis que fan altres navegadors
        subscribe(context) {
            if (subscribed) return;
            subscribed = true;

            let connectedBefore = false;
            supabase
                .channel("race-control")
                .on(
                    "postgres_changes",
                    { event: "*", schema: "public", table: "categories" },
                    (payload) => {
                        if (payload.eventType === "DELETE") return;
                        context.commit("categories/applyRows", [payload.new]);
                    }
                )
                .on(
                    "postgres_changes",
                    { event: "*", schema: "public", table: "racers" },
                    (payload) => {
                        if (payload.eventType === "DELETE") {
                            context.commit("racers/removeRacer", payload.old.id);
                            return;
                        }
                        context.commit("racers/applyRows", [payload.new]);
                    }
                )
                .subscribe((status) => {
                    if (status === "SUBSCRIBED") {
                        // En reconnectar no arriben els canvis perduts: cal tornar a llegir-ho tot
                        if (connectedBefore) context.dispatch("resync");
                        connectedBefore = true;
                        context.commit("sync/setOnline", true);
                    } else {
                        context.commit("sync/setOnline", false);
                    }
                });

            window.addEventListener("online", () => context.dispatch("resync"));
            document.addEventListener("visibilitychange", () => {
                if (document.visibilityState === "visible") context.dispatch("resync");
            });
        },
    },
    modules: { categories, racers, sync },
    plugins: [persistPlugin],
});
